import { useMemo } from 'react';
import WrapperDataGrid from '@modules/dataGrid/WrapperDataGrid';
import { GridColDef } from '@mui/x-data-grid-pro';
import { IRouterProps } from '@interfaces/App.interface';
import {
  generateGlobalColumns,
  generateBooleanColumn,
  generateEntityColumn,
  generateMultiEntityColumn,
  IMultiEntitySelectorCellProps,
  IDataGridConfig,
} from '@ts/storybook';
import UserRoles, { defaultConfigComponent as URConfigComponent } from './UserRoles';
import { useAppSelector } from '@redux/store';
import { CElement, CUser, COrganization, CRole, IConfigComponent } from '@ts/ui-models';
import actStore from '@flux/actions/actStore';

interface IUsersProps extends IRouterProps {
  configComponent?: IConfigComponent;
  inheritedProps?: any;
}

export const defaultConfigComponent: IConfigComponent = {
  componentId: 'users',
  aclId: 'taUser',
  entity: 'user',
  fetchURL: '/api/users',
  crudURL: '/api/user',
  mainClass: CUser,
};

const Users = (props: IUsersProps) => {
  console.log('props', props);

  const { configComponent = defaultConfigComponent, inheritedProps } = props;
  const config: IDataGridConfig = {
    columnVisibilityModel: { id: false },
  };

  const badge = useAppSelector(state => state.global.sessionBadge);

  const onClickMultiEntityCell = (props: IMultiEntitySelectorCellProps, listIds: string[]) => {
    const { params } = props;
    const { optionsColumn } = props.options;
    const roles = {
      component: UserRoles,
      configComponent: {
        ...URConfigComponent,
        fetchURL: `/api/v2/user/${badge?.userId}/organization/${badge?.organizationId}/roles`,
      },
    };

    actStore.setActionTableV3({
      title: optionsColumn?.headerName + (params.row.getName() !== 'Unknown' ? ' - ' + params.row.getName() : ''),
      action: 'openModalExtraComp',
      component: roles.component,
      propsComponent: roles.configComponent,
      row: params.row,
    });
  };

  const getColumns = useMemo((): GridColDef<any>[] => {
    const mixProps = { ...props, configComponent };
    const globalCells = generateGlobalColumns(mixProps);
    return [
      globalCells.id(),
      globalCells.name('Login / ID', CUser),
      {
        field: 'fullname',
        type: 'string',
        headerName: 'Full name',
        valueGetter: (value, row, column) => {
          const tUser = new CUser(row);
          return tUser.getName();
        },
      },
      generateEntityColumn({ field: 'organization' }, { field: 'organization', mainClass: COrganization }, { inheritedProps }),
      generateBooleanColumn({ field: 'isEnabled', headerName: 'Enabled' }, { field: 'isEnabled' }),
      {
        headerName: 'Group',
        field: 'groups',
        valueGetter: (value, row, column) => {
          const tElement = new CElement(row);
          return tElement.xItemTags?.map(xItemTag => xItemTag.tag.name).join('; ');
        },
      },
      generateMultiEntityColumn(
        { field: 'acls', headerName: 'Roles' },
        { field: 'acls', tEntity: 'role', mainClass: CRole },
        {
          inheritedProps,
          chips: { limitChips: 3 },
          onClick: onClickMultiEntityCell,
        }
      ),
    ];
  }, [configComponent]);

  return (
    <WrapperDataGrid
      configComponent={{
        ...configComponent,
        filtersFetchData: { role: { ownerOrganizationId: badge?.organizationId }, ...configComponent.filtersFetchData },
      }}
      dataGridConfig={config}
      columns={getColumns}
      //Extra data
      inheritedProps={inheritedProps}
    />
  );
};

export default Users;
